import { Text } from '@hotjar/hotsauce';
import { Recording } from '@hotjar/webapp/domains/recordings/types';
import { Card } from '@hotjar/webapp/components/Card';
import { useCurrentSite } from '@hotjar/site';
import { useTranslation } from 'react-i18next';
import { RecordingsOverviewCardList } from '../RecordingsOverviewCardList';
import { OverviewTrackingElements } from '../../RecordingsPage/utils';
import { navigateToViewAllRecordings } from '../utils/navigateToViewAllRecordings';
import { WidgetEmptyState } from '../EmptyState/WidgetEmptyState';
import {
  StyledHeader,
  StyledHeaderTextContainer,
  StyledLink,
  StyledFullWidthOverviewWidget,
  StyledOverriddenState,
  StyledText,
} from '../styles';

import type { ReactNode } from 'react';
import type { RecommendedWidgetConfig } from '../RecordingsOverview';

type RecordingsOverviewWidgetProps = {
  recordings: Recording[];
  widgetConfig: RecommendedWidgetConfig;
  count: number;
  onPlayClick: (widgetKey: string, renderPosition: number, elementName: string) => void;
  trackOnViewAll: (widgetKey: string, elementName: string) => Promise<void>;
  children: ReactNode;
  overriddenCardState?: JSX.Element | null;
};

export const RecordingsOverviewWidget = ({
  recordings,
  widgetConfig,
  onPlayClick,
  trackOnViewAll,
  children,
  count,
  overriddenCardState = null,
}: RecordingsOverviewWidgetProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { widgetKey, sortBy, filters, emptyStateImage, color } = widgetConfig;

  const title = t(`common:recordings.recordings-overview.widget.${widgetKey}.title`);
  const subtitle = t(`common:recordings.recordings-overview.widget.${widgetKey}.subtitle`);
  const description = t(`common:recordings.recordings-overview.widget.${widgetKey}.description`);
  const { data: currentSite } = useCurrentSite();

  if (!currentSite?.id) {
    return null;
  }

  const onViewAllClick = async (elementName: string) => {
    await trackOnViewAll(widgetKey, elementName);
    navigateToViewAllRecordings(currentSite.id, widgetConfig);
  };

  if (overriddenCardState !== null) {
    return (
      <StyledFullWidthOverviewWidget>
        <Card>
          <StyledHeader>
            <StyledHeaderTextContainer>
              <StyledText as="h2" textType="header5">
                {title}
              </StyledText>
              <Text role="note" as="span" textColor="text_light" textType="caption">
                {subtitle}
              </Text>
            </StyledHeaderTextContainer>
          </StyledHeader>
          <StyledOverriddenState>{overriddenCardState}</StyledOverriddenState>
        </Card>
      </StyledFullWidthOverviewWidget>
    );
  }

  return (
    <StyledFullWidthOverviewWidget>
      <Card>
        {recordings.length > 0 ? (
          <>
            <StyledHeader>
              <StyledHeaderTextContainer>
                <StyledLink
                  as="button"
                  kind="standalone"
                  onClick={() => onViewAllClick(OverviewTrackingElements.WIDGET_HEADING)}
                >
                  {title}
                </StyledLink>
                <Text role="note" as="span" textColor="text_light" textType="caption">
                  {subtitle}
                </Text>
              </StyledHeaderTextContainer>
            </StyledHeader>
            <RecordingsOverviewCardList
              recordingsList={recordings}
              sortBy={sortBy}
              filters={filters}
              onPlayClick={(renderPosition: number, elementName: string) =>
                onPlayClick(widgetKey, renderPosition, elementName)
              }
              onViewAllClick={(elementName) => onViewAllClick(elementName)}
              count={count}
            >
              {children}
            </RecordingsOverviewCardList>
          </>
        ) : (
          <>
            <WidgetEmptyState
              image={emptyStateImage}
              color={color}
              title={title}
              description={description}
            />
          </>
        )}
      </Card>
    </StyledFullWidthOverviewWidget>
  );
};
